import { ContractError, IN_USE_IDLE_MS, createApiClient, type Platform, type PlatformEvent } from '@sd/client-core';
import { ApiError, api, type ServerInfo } from '@sd/contracts';
import type { DesktopLoginResult } from '@sd/contracts/ipc';

/** "웹 브라우저 · Chrome" — shown in the device list. */
function browserLabel(ua: string = navigator.userAgent): string {
  const name = /Edg\//.test(ua)
    ? 'Edge'
    : /Whale\//.test(ua)
      ? 'Whale'
      : /SamsungBrowser\//.test(ua)
        ? 'Samsung Internet'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Chrome\//.test(ua)
            ? 'Chrome'
            : /Safari\//.test(ua)
              ? 'Safari'
              : '브라우저';
  return `웹 브라우저 · ${name}`;
}

function failure(error: unknown): DesktopLoginResult {
  if (error instanceof ApiError) return { status: 'error', code: error.code, message: error.message };
  // This page was loaded before the server was updated: a reload gets the matching web client.
  if (error instanceof ContractError) return { status: 'error', code: 'UNSUPPORTED_CLIENT', message: error.message };
  return { status: 'error', code: 'NETWORK', message: '' };
}

/**
 * The service worker (public/sw.js): shows pushed and the page's own notifications (Android shows no
 * other kind) and brings clicks on them back here. Resolves to null where there is none.
 */
function serviceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return Promise.resolve(null);
  return navigator.serviceWorker.register('/sw.js').then(
    () => navigator.serviceWorker.ready,
    () => null,
  );
}

const ICON = { icon: '/icons/icon-192.png', badge: '/icons/badge-96.png' };

/** Opened as the installed app (from the home screen or an app window), not in a browser tab. */
const installedApp = () => ['standalone', 'fullscreen', 'minimal-ui'].some((mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches === true);

/**
 * Tells the service worker the app is on this device, so a notification click on Android opens it rather than a
 * browser tab (public/sw.js `APP_MARK`). Written each time the app opens; it holds only the time.
 */
function markInstalledApp() {
  if (!installedApp() || typeof caches === 'undefined') return;
  void caches
    .open('sd-state')
    .then((cache) => cache.put('/__sd/installed-app', new Response(new Date().toISOString())))
    .catch(() => {});
}

/** A conversation the app was opened for from a notification (`/?conversation=<id>`), taken out of the address. */
function launchConversation(): string | null {
  const url = new URL(window.location.href);
  const id = url.searchParams.get('conversation');
  if (!id) return null;
  url.searchParams.delete('conversation');
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  return id;
}

const sameKey = (a: ArrayBuffer | null, b: Uint8Array) => a !== null && a.byteLength === b.byteLength && new Uint8Array(a).every((byte, i) => byte === b[i]);

/** Answers to a logout that mean the server no longer accepts this session anyway. */
const SESSION_ALREADY_ENDED = new Set(['UNAUTHENTICATED', 'SESSION_REVOKED', 'TOKEN_EXPIRED', 'ACCOUNT_DEACTIVATED']);

/**
 * Browser host for the shared UI. The page is served by the company server
 * itself, so every call is same-origin with the HttpOnly session cookie and
 * the CSRF header. Nothing sensitive is persisted in the browser: pending
 * sends and drafts live in memory only, and there is no settings/quit.
 */
export function createWebPlatform(origin: string = window.location.origin): Platform {
  const transport = { baseUrl: origin, auth: { kind: 'cookie' as const } };
  const http = createApiClient(transport);
  const listeners = new Set<(event: PlatformEvent) => void>();
  const emit = (event: PlatformEvent) => listeners.forEach((l) => l(event));
  let info: Promise<ServerInfo> | null = null;
  const serverInfo = () => {
    info ??= http.call(api.meta.serverInfo).catch((error: unknown) => {
      info = null;
      throw error;
    });
    return info;
  };
  window.addEventListener('online', () => emit({ type: 'network.online' }));
  window.addEventListener('offline', () => emit({ type: 'network.offline' }));
  document.addEventListener('visibilitychange', () => emit({ type: 'visibility', visible: document.visibilityState === 'visible' }));
  // When the person last used the page: one left open in front of nobody does not hold their pushes (inUse).
  let usedAt = Date.now();
  for (const type of ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart']) window.addEventListener(type, () => (usedAt = Date.now()), { capture: true, passive: true });
  const client = { kind: 'web' as const, platform: 'web' as const, deviceLabel: browserLabel(), appVersion: __APP_VERSION__ };
  /** Notifications on screen or in the OS notification centre, one per conversation (a newer one replaces it by tag). */
  const shown = new Map<string, Notification>();
  const worker = serviceWorker();
  let launched = launchConversation();
  if ('serviceWorker' in navigator) {
    markInstalledApp();
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent<{ type?: string; conversationId?: string }>) => {
      // A notification was clicked: the service worker asks which open window is the installed app.
      if (event.data?.type === 'sd.display') {
        event.ports[0]?.postMessage({ app: installedApp() });
        return;
      }
      const conversationId = event.data?.conversationId;
      if (event.data?.type !== 'sd.notification.clicked' || !conversationId) return;
      void serverInfo().then((s) => emit({ type: 'notification.clicked', serverId: s.instanceId, conversationId }));
    });
  }

  return {
    kind: 'web',
    appInfo: async () => ({ appVersion: __APP_VERSION__, platform: 'web', secureStorage: 'none', titlebar: { kind: 'none', leftInset: 0, rightInset: 0 } }),
    listServers: async () => {
      // Always fresh: the owner may have renamed the company since the page loaded.
      const fresh = http.call(api.meta.serverInfo);
      const s = await fresh;
      info = fresh;
      return [{ serverId: s.instanceId, companyName: s.companyName, serverUrl: s.serverUrl, signedIn: true, account: null }];
    },
    getActiveServerId: async () => (await serverInfo()).instanceId,
    setActiveServer: async () => {},
    serverRegistry: null,
    transport: () => transport,
    auth: {
      login: async (_serverId, email, password) => {
        try {
          const result = await http.call(api.auth.login, { body: { email, password, client } });
          if (result.status === 'unsupported') return { status: 'error', code: 'UNSUPPORTED_CLIENT', message: 'the server was updated' };
          return result.status === 'ok' ? { status: 'ok', me: result.me } : result;
        } catch (error) {
          return failure(error);
        }
      },
      verifyMfa: async (_serverId, challengeToken, input) => {
        try {
          const result = await http.call(api.auth.verifyMfa, { body: { challengeToken, ...input } });
          return { status: 'ok', me: result.me };
        } catch (error) {
          return failure(error);
        }
      },
      startMfaSetup: (_serverId, challengeToken) => http.call(api.auth.startMfaSetup, { body: { challengeToken } }),
      confirmMfaSetup: async (_serverId, challengeToken, code) => {
        try {
          const result = await http.call(api.auth.confirmMfaSetup, { body: { challengeToken, code } });
          return { status: 'ok', me: result.me, ...(result.recoveryCodes ? { recoveryCodes: result.recoveryCodes } : {}) };
        } catch (error) {
          return failure(error);
        }
      },
      // Only the server can delete the HttpOnly session cookie: until it confirms, this browser is
      // still signed in, so a failed logout (offline, server restarting) must not look like one that worked.
      logout: async () => {
        try {
          await http.call(api.auth.logout);
        } catch (error) {
          if (error instanceof ApiError && SESSION_ALREADY_ENDED.has(error.code)) return;
          throw error;
        }
      },
    },
    download: async ({ fileId, fileName, acknowledgeUnscanned }) => {
      // The browser streams it with the session cookie; completion is only visible in the browser.
      const a = document.createElement('a');
      a.href = http.url(api.files.download, { params: { fileId }, query: acknowledgeUnscanned ? { acknowledgeUnscanned: 'true' } : undefined });
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return { status: 'browser' };
    },
    mediaUrl: (fileId) => http.url(api.files.preview, { params: { fileId } }),
    showInFolder: async () => {},
    notify: async ({ serverId, conversationId, title, body, messageId }) => {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      if (document.visibilityState === 'visible' && document.hasFocus()) return;
      const registration = await worker;
      if (registration) {
        // A push for the same message may have shown it already.
        if (messageId && (await registration.getNotifications({ tag: conversationId })).some((n) => (n.data as { messageId?: string } | null)?.messageId === messageId)) return;
        await registration.showNotification(title, { body, tag: conversationId, renotify: true, data: { conversationId, messageId: messageId ?? null }, ...ICON } as NotificationOptions);
        return;
      }
      const n = new Notification(title, { body, tag: conversationId });
      shown.set(conversationId, n);
      n.onclick = () => {
        window.focus();
        emit({ type: 'notification.clicked', serverId, conversationId });
        n.close();
      };
      n.onclose = () => {
        if (shown.get(conversationId) === n) shown.delete(conversationId);
      };
    },
    // The page outlives a logout: what the notification centre still shows must go with the session.
    withdrawNotifications: async (_serverId, conversationId) => {
      for (const [id, n] of shown) {
        if (conversationId !== undefined && id !== conversationId) continue;
        shown.delete(id);
        n.close();
      }
      const registration = await worker;
      for (const n of (await registration?.getNotifications(conversationId === undefined ? {} : { tag: conversationId })) ?? []) n.close();
    },
    push:
      'PushManager' in window
        ? {
            permission: () => (typeof Notification === 'undefined' ? 'denied' : Notification.permission),
            subscribe: async (publicKey) => {
              const registration = await worker;
              if (!registration) return null;
              const key = Uint8Array.from(atob(publicKey.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
              let subscription = await registration.pushManager.getSubscription();
              // Made for another key (the company server was installed again): it can no longer be used.
              if (subscription && !sameKey(subscription.options.applicationServerKey, key)) {
                await subscription.unsubscribe();
                subscription = null;
              }
              subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
              const json = subscription.toJSON();
              return json.endpoint && json.keys?.p256dh && json.keys.auth ? { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } } : null;
            },
          }
        : null,
    takeLaunchConversation: () => {
      const id = launched;
      launched = null;
      return id;
    },
    setDoNotDisturb: async () => {},
    inUse: async () => document.visibilityState === 'visible' && document.hasFocus() && Date.now() - usedAt < IN_USE_IDLE_MS,
    setBadge: (count) => {
      const base = document.title.replace(/^\(\d+\+?\)\s*/, '');
      document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${base}` : base;
      // The app icon too, where there is one: an iPhone home-screen app (with notifications allowed) or an installed app on a PC.
      if ('setAppBadge' in navigator) void (count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge()).catch(() => {});
    },
    persistence: null,
    settings: null,
    quit: null,
    updates: null,
    onEvent: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
